export type LeadStage =
  | 'new'
  | 'contacted'
  | 'qualified'
  | 'proposal_sent'
  | 'won'
  | 'lost'

export const LEAD_STAGES: { value: LeadStage; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
]

export function leadSourceNeedsDetail(sourceName?: string | null) {
  const n = (sourceName || '').trim().toLowerCase()
  return n === 'other' || n === 'referral'
}

export function formatLeadStage(stage?: string | null) {
  if (!stage) return 'N/A'
  return stage.charAt(0).toUpperCase() + stage.slice(1).replace(/_/g, ' ')
}

export function leadStageBadgeClass(stage?: string | null) {
  switch (stage) {
    case 'qualified':
      return 'bg-green-100 text-green-800'
    case 'contacted':
      return 'bg-blue-100 text-blue-800'
    case 'proposal_sent':
      return 'bg-purple-100 text-purple-800'
    case 'won':
      return 'bg-emerald-100 text-emerald-800'
    case 'lost':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

export interface ContactNameFields {
  first_name?: string
  last_name?: string
  name?: string
}

export function normalizeContactFields(c: ContactNameFields) {
  if (c.first_name || c.last_name) {
    const first_name = c.first_name || ''
    const last_name = c.last_name || ''
    return {
      first_name,
      last_name,
      name: `${first_name} ${last_name}`.trim(),
    }
  }
  if (c.name?.trim()) {
    const parts = c.name.trim().split(' ')
    return {
      first_name: parts[0] || '',
      last_name: parts.slice(1).join(' ') || '',
      name: c.name.trim(),
    }
  }
  return { first_name: '', last_name: '', name: '' }
}

export function getContactDisplayName(c: ContactNameFields) {
  const normalized = normalizeContactFields(c)
  return normalized.name || 'Unnamed Contact'
}

export function contactHasName(c: ContactNameFields) {
  const normalized = normalizeContactFields(c)
  return !!(normalized.first_name || normalized.last_name)
}

/** True when a lead project row has enough data to persist */
export function projectHasContent(project: {
  address?: string
  type?: string
  name?: string
  city?: string
}): boolean {
  return !!(
    project.address?.trim() ||
    project.type?.trim() ||
    project.name?.trim() ||
    project.city?.trim()
  )
}

/** Keep projects that have address, type, or other identifying info */
export function serializeLeadProjects<T extends { address?: string; type?: string; name?: string; city?: string }>(
  projects: T[]
): T[] {
  return projects.filter(projectHasContent)
}

const ASSIGNED_TO_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Normalize assigned_to to a UUID string or null (empty/invalid → null) */
export function normalizeAssignedToId(value: unknown): string | null {
  if (value == null || typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed || !ASSIGNED_TO_UUID_RE.test(trimmed)) return null
  return trimmed
}

export interface LeadManagementFormData {
  lead_source_id: string
  lead_source_other: string
  assigned_to: string
  next_activity_date: string
  stage?: LeadStage
}
